import React from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Header } from './header'
import { MainSidebar } from './sidebar'
import { DemandProvider } from '../../contexts'
import { ModalManagerProvider } from '../Modal/ModalManager'
import { Breadcrumb } from '../Breadcrumb'
import { cn } from '../../utils/formatter'
import { WavesBackground } from './WavesBackground'
import { KeyboardShortcutsHelpModal } from '../KeyboardShortcuts/HelpModal'
import { useKeyboardShortcutsContext } from '../../contexts/KeyboardShortcutsContext'
import { useHotkeys } from 'react-hotkeys-hook'

// Componente SidebarInset equivalente - Conteúdo sobre as ondas
const SidebarInset = ({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn(
        'flex flex-1 flex-col min-h-0',
        // mobile
        'mx-0 mb-0 mt-0 rounded-none',
        // desktop
        'md:mr-4 md:mb-4 md:mt-4 md:rounded-2xl',
        'shadow-[0_20px_70px_-15px_rgba(0,0,0,0.4)] border border-gray-200/40 dark:border-neutral-800/40 overflow-hidden backdrop-blur-sm',
        'hover:shadow-[0_25px_80px_-15px_rgba(0,0,0,0.45)] transition-shadow duration-500',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

const LayoutContent = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { showHelpModal, hideHelpModal, isHelpModalVisible, markSequenceSuccess } =
    useKeyboardShortcutsContext()
  const [waitingForGestaoKey, setWaitingForGestaoKey] = React.useState(false)
  const [waitingForDemandKey, setWaitingForDemandKey] = React.useState(false)

  // Reset gestao key waiting state after timeout
  React.useEffect(() => {
    if (waitingForGestaoKey) {
      const timeout = setTimeout(() => {
        setWaitingForGestaoKey(false)
      }, 2000) // 2 seconds timeout
      return () => clearTimeout(timeout)
    }
    return undefined
  }, [waitingForGestaoKey])

  // Reset demand key waiting state after timeout
  React.useEffect(() => {
    if (waitingForDemandKey) {
      const timeout = setTimeout(() => {
        setWaitingForDemandKey(false)
      }, 2000) // 2 seconds timeout
      return () => clearTimeout(timeout)
    }
    return undefined
  }, [waitingForDemandKey])

  // Global keyboard shortcuts with proper preventDefault
  useHotkeys(
    'ctrl+h',
    event => {
      event.preventDefault()
      console.log('🔥 Ctrl+H pressed - showing help modal')
      showHelpModal()
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
    }
  )

  useHotkeys(
    'f1',
    event => {
      event.preventDefault()
      console.log('🔥 F1 pressed - showing help modal')
      showHelpModal()
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
    }
  )

  // Navigation shortcuts - single keys
  // useHotkeys('h', (event) => {
  //   event.preventDefault()
  //   console.log('🔥 H pressed - navigating to home');
  //   navigate('/')
  // }, {
  //   preventDefault: true,
  //   enableOnFormTags: false
  // });

  // useHotkeys('c', (event) => {
  //   event.preventDefault()
  //   console.log('🔥 C pressed - navigating to collaborators');
  //   navigate('/collaborators')
  // }, {
  //   preventDefault: true,
  //   enableOnFormTags: false
  // });

  // useHotkeys('n', (event) => {
  //   event.preventDefault()
  //   console.log('🔥 N pressed - navigating to notifications');
  //   navigate('/notificacoes')
  // }, {
  //   preventDefault: true,
  //   enableOnFormTags: false
  // });

  // Alternative shortcuts using different keys
  useHotkeys(
    'ctrl+alt+n',
    event => {
      event.preventDefault()
      console.log('🔥 Ctrl+Alt+N pressed - creating new demand')
      navigate('/demands/new')
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
    }
  )

  // Demand shortcuts - sequence mode (Ctrl+D then letter)
  useHotkeys(
    'ctrl+d',
    event => {
      event.preventDefault()
      console.log('🔥 Ctrl+D pressed - waiting for demand key')
      setWaitingForDemandKey(true)
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
    }
  )

  // Demand sequence keys (only work after Ctrl+D)
  useHotkeys(
    'l',
    event => {
      if (waitingForDemandKey) {
        event.preventDefault()
        console.log('🔥 Ctrl+D, L pressed - viewing all demands')
        navigate('/demands')
        markSequenceSuccess('Todas as Demandas')
        setWaitingForDemandKey(false)
      }
    },
    {
      preventDefault: false,
      enableOnFormTags: false,
      enabled: waitingForDemandKey,
    }
  )

  useHotkeys(
    'r',
    event => {
      if (waitingForDemandKey) {
        event.preventDefault()
        console.log('🔥 Ctrl+D, R pressed - viewing received demands')
        navigate('/demands/received')
        markSequenceSuccess('Demandas Recebidas')
        setWaitingForDemandKey(false)
      }
    },
    {
      preventDefault: false,
      enableOnFormTags: false,
      enabled: waitingForDemandKey,
    }
  )

  useHotkeys(
    'e',
    event => {
      if (waitingForDemandKey) {
        event.preventDefault()
        console.log('🔥 Ctrl+D, E pressed - viewing sent demands')
        navigate('/demands/sent')
        markSequenceSuccess('Demandas Enviadas')
        setWaitingForDemandKey(false)
      }
    },
    {
      preventDefault: false,
      enableOnFormTags: false,
      enabled: waitingForDemandKey,
    }
  )

  useHotkeys(
    'a',
    event => {
      if (waitingForDemandKey) {
        event.preventDefault()
        console.log('🔥 Ctrl+D, A pressed - viewing all demands (alternative)')
        navigate('/demands')
        markSequenceSuccess('Todas as Demandas')
        setWaitingForDemandKey(false)
      }
    },
    {
      preventDefault: false,
      enableOnFormTags: false,
      enabled: waitingForDemandKey,
    }
  )

  // Gestao shortcuts - sequence mode (Ctrl+G then letter)
  useHotkeys(
    'ctrl+g',
    event => {
      event.preventDefault()
      console.log('🔥 Ctrl+G pressed - waiting for gestao key')
      setWaitingForGestaoKey(true)
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
    }
  )

  // Gestao sequence keys (only work after Ctrl+G)
  useHotkeys(
    'p',
    event => {
      if (waitingForGestaoKey) {
        event.preventDefault()
        console.log('🔥 Ctrl+G, P pressed - going to processes')
        navigate('/gestao/processos')
        markSequenceSuccess('Gestão de Processos')
        setWaitingForGestaoKey(false)
      }
    },
    {
      preventDefault: false,
      enableOnFormTags: false,
      enabled: waitingForGestaoKey,
    }
  )

  useHotkeys(
    's',
    event => {
      if (waitingForGestaoKey) {
        event.preventDefault()
        console.log('🔥 Ctrl+G, S pressed - going to sectors')
        navigate('/gestao/setores')
        markSequenceSuccess('Gestão de Setores')
        setWaitingForGestaoKey(false)
      }
    },
    {
      preventDefault: false,
      enableOnFormTags: false,
      enabled: waitingForGestaoKey,
    }
  )

  useHotkeys(
    'e',
    event => {
      if (waitingForGestaoKey) {
        event.preventDefault()
        console.log('🔥 Ctrl+G, E pressed - going to teams')
        navigate('/gestao/equipes')
        markSequenceSuccess('Gestão de Equipes')
        setWaitingForGestaoKey(false)
      }
    },
    {
      preventDefault: false,
      enableOnFormTags: false,
      enabled: waitingForGestaoKey,
    }
  )

  useHotkeys(
    'a',
    event => {
      if (waitingForGestaoKey) {
        event.preventDefault()
        console.log('🔥 Ctrl+G, A pressed - going to authorizers')
        navigate('/gestao/autorizadores')
        markSequenceSuccess('Gestão de Autorizadores')
        setWaitingForGestaoKey(false)
      }
    },
    {
      preventDefault: false,
      enableOnFormTags: false,
      enabled: waitingForGestaoKey,
    }
  )
  const noPaddingRoutes = [
    '/demands',
    '/demands/new',
    '/demands/received',
    '/demands/sent',
    '/demands/waitingAuth',
    '/demands/toAssign',
    '/demands/inProgress',
    '/demands/solved',
    '/demands/finished',
    '/demands/watching',
    '/demands/authorization',
    '/notificacoes',
    '/calendar',
  ]

  // Gerar breadcrumbs baseado na rota atual
  const generateBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const breadcrumbs = []

    // Mapeamento de rotas para nomes amigáveis
    const routeNames: Record<string, string> = {
      // Dashboard
      '': 'Dashboard',

      // Demandas
      demands: 'Demandas',
      new: 'Criar',
      view: 'Visualizar',
      edit: 'Editar',
      received: 'Recebidas',
      sent: 'Enviadas',
      watching: 'Observando',
      toAssign: 'Em Atribuição',
      authorization: 'Autorizações',
      waitingAuth: 'Aguardando Autorização',
      inProgress: 'Em Progresso',
      solved: 'Solucionadas',
      finished: 'Finalizadas',

      // Gestão
      gestao: 'Gestão',
      processos: 'Processos',
      setores: 'Setores',
      equipes: 'Equipes',
      autorizadores: 'Autorizadores',

      // Outras páginas
      collaborators: 'Colaboradores',
      components: 'Componentes',
      notificacoes: 'Notificações',
      calendar: 'Calendário',
    }

    // Segmentos que não têm rota própria (apenas categorias)
    const nonClickableSegments = ['gestao']

    // Se estiver na home, não mostrar breadcrumbs
    if (pathSegments.length === 0) {
      return []
    }

    // Adicionar Dashboard como primeiro item
    breadcrumbs.push({ label: 'Dashboard', href: '/' })

    // Construir breadcrumbs baseado nos segmentos
    let currentPath = ''
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`
      const isLast = index === pathSegments.length - 1

      // Verificar se é um ID numérico (não adicionar ao breadcrumb)
      if (/^\d+$/.test(segment)) {
        return
      }

      const label = routeNames[segment] || segment.charAt(0).toUpperCase() + segment.slice(1)

      // Determinar se o item deve ser clicável
      const isClickable = !isLast && !nonClickableSegments.includes(segment)

      breadcrumbs.push({
        label,
        href: isClickable ? currentPath : undefined,
        isLast,
      })
    })

    return breadcrumbs
  }

  const breadcrumbs = generateBreadcrumbs()

  return (
    <div className='h-screen flex flex-col md:flex-row relative overflow-hidden'>
      {/* Background com ondas animadas */}
      <WavesBackground />

      {/* Sidebar - Level abaixo, com leve transparência */}
      <div className='fixed md:relative z-10'>
        <MainSidebar />
      </div>

      {/* Main Content Area - Floating sobre as ondas */}
      <SidebarInset className='shadow-2xl'>
        {/* Header com Breadcrumbs */}
        <header className='flex h-14 md:h-12 shrink-0 items-center gap-2 backdrop-blur-md relative z-[100] '>
          <div className='flex items-center gap-2 md:gap-4 px-3 md:px-6 w-full'>
            {/* Breadcrumbs */}
            {breadcrumbs.length > 0 && <Breadcrumb items={breadcrumbs} />}

            {/* Header simplificado */}
            <div className='ml-auto'>
              <Header />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className='flex-1 overflow-auto'>
          <div
            className={cn(!noPaddingRoutes.includes(location.pathname) && 'px-3 md:pl-4 md:pr-4 pt-1 pb-4')}
          >
            <Outlet />
          </div>
        </main>
      </SidebarInset>

      {/* Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsHelpModal isOpen={isHelpModalVisible} onClose={hideHelpModal} />
    </div>
  )
}

export const MainLayout = () => {
  return (
    <DemandProvider>
      <ModalManagerProvider>
        <LayoutContent />
      </ModalManagerProvider>
    </DemandProvider>
  )
}
